import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Box, Typography, Button, CircularProgress } from '@mui/joy'
import { ArrowLeft as ArrowBack } from '@phosphor-icons/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { DocumentForm } from '../components/billing'
import { estimatesApi, taxRatesApi } from '../lib/billingApi'
import { apiClient } from '../lib/api'
import type { TaxRate } from '../types/billing'

export default function EstimateCreate() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const opportunityId = searchParams.get('opportunity')
  // Job Detail's "Create Estimate" opens /billing/estimates/new?job=<id>.
  const jobId = searchParams.get('job')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Try to get pre-populated data from navigation state or sessionStorage
  const [prePopulatedData, setPrePopulatedData] = useState<{
    measurement_id?: number
    address?: string
    customer_id?: number
    total_squares?: number
    total_price?: number
    pitch?: string
    complexity?: string
    line_items?: Array<{
      description: string
      notes?: string
      quantity: number
      unit_price: number
      item_type: string
      is_taxable: boolean
      tax_rate: number | null
      total: number
      sort_order: number
      is_bold?: boolean
      material_spec_id?: number
      calculation_data?: any
    }>
    subtotal?: number
    tax_amount?: number
    material_selections?: any
    calculations?: any
  } | null>(location.state || null)

  // Check sessionStorage for data from measurements app
  useEffect(() => {
    if (!prePopulatedData) {
      // Check for detailed estimate data from measurements app
      const detailedData = sessionStorage.getItem('detailedEstimateData')
      const simpleData = sessionStorage.getItem('estimateData')
      
      if (detailedData) {
        try {
          const parsed = JSON.parse(detailedData)
          console.log('Found detailed estimate data:', parsed)
          setPrePopulatedData(parsed)
          // Clear the data after reading it
          sessionStorage.removeItem('detailedEstimateData')
        } catch (e) {
          console.error('Failed to parse detailed estimate data from sessionStorage:', e)
        }
      } else if (simpleData) {
        try {
          setPrePopulatedData(JSON.parse(simpleData))
          sessionStorage.removeItem('estimateData')
        } catch (e) {
          console.error('Failed to parse estimate data from sessionStorage:', e)
        }
      }
    }
  }, [])

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await apiClient.getContacts({ page_size: 1000 })
      // Response.data is already the results array for paginated responses
      return response.data
    }
  })

  // Fetch jobs (include completed so estimates can link after close-out)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', { includeCompleted: true, pageSize: 1000 }],
    queryFn: async () => {
      const response = await apiClient.getJobs(undefined, true, 1000)
      return response.data
    }
  })

  // Fetch opportunities
  const { data: opportunitiesData, isLoading: opportunitiesLoading } = useQuery({
    queryKey: ['opportunities'],
    queryFn: async () => {
      try {
        const response = await apiClient.getOpportunities()
        // Ensure we always return an array
        return Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Failed to fetch opportunities:', error)
        // Return empty array on error to prevent crashes
        return []
      }
    },
    retry: false, // Don't retry on failure
    staleTime: 5 * 60 * 1000 // Consider data stale after 5 minutes
  })

  // Fetch tax rates
  const { data: taxRates = [], isLoading: taxRatesLoading } = useQuery<TaxRate[]>({
    queryKey: ['taxRates'],
    queryFn: async () => {
      const response = await taxRatesApi.list()
      return response.results
    }
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => estimatesApi.create(data),
    onSuccess: (estimate: any) => {
      console.log('Estimate created:', estimate)
      console.log('Current location:', location.pathname)
      toast.success('Estimate created successfully')
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      navigate(`/operations/billing/estimates/${estimate.id}`)
    },
    onError: (error: any) => {
      console.error('Create estimate error details:', error.response?.data)
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.line_items?.[0] ||
                          JSON.stringify(error.response?.data) ||
                          'Failed to create estimate'
      toast.error(errorMessage)
      setIsSubmitting(false)
    }
  })

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true)
    
    // Prepare the data for POST request
    const createData = {
      customer: data.customer,
      job: data.job || null,
      opportunity: data.opportunity || null,
      document_date: data.document_date,
      due_date: data.due_date || null,
      valid_until: data.valid_until || null,
      payment_terms: data.payment_terms,
      custom_payment_terms: data.custom_payment_terms || '',
      terms_and_conditions: data.terms_and_conditions || '',
      notes: data.notes || '',
      internal_notes: data.internal_notes || '',
      discount_amount: data.discount_amount || 0,
      // Overhead and profit fields
      include_overhead: data.include_overhead || false,
      overhead_percentage: data.overhead_percentage || null,
      include_profit: data.include_profit || false,
      profit_percentage: data.profit_percentage || null,
      // Sent whole: the map that used to stand here dropped taxable_pct, the
      // catalog linkage and the internal sub-items on the way out.
      line_items: data.line_items
    }

    console.log('Sending estimate data:', createData)
    console.log('Line items:', createData.line_items)
    
    try {
      await createMutation.mutateAsync(createData)
    } catch (error) {
      throw error // Keep the form draft and show server validation fields.
    }
  }

  const handleCancel = () => {
    navigate(opportunityId ? `/crm/opportunities/${opportunityId}` : '/operations/billing/estimates')
  }

  // Don't wait for opportunities since they're optional
  const isLoading = customersLoading || jobsLoading || taxRatesLoading

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  // customersData, jobsData, and opportunitiesData are already the results arrays due to ApiClient extracting them
  const customers = customersData || []
  const jobs = jobsData || []
  const opportunities = opportunitiesData || []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to={opportunityId ? `/crm/opportunities/${opportunityId}` : '/operations/billing/estimates'}
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to {opportunityId ? 'Opportunity' : 'Estimates'}
        </Button>
        
        <Typography level="h1">Create New Estimate</Typography>
      </Box>
      
      <DocumentForm
        documentType="estimate"
        customers={customers}
        jobs={jobs}
        opportunities={opportunities}
        taxRates={taxRates}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
        initialCustomerId={prePopulatedData?.customer_id}
        initialJobId={jobId ? Number(jobId) : undefined}
        initialOpportunityId={opportunityId ? Number(opportunityId) : undefined}
        initialLineItems={prePopulatedData?.line_items || undefined}
      />
    </Box>
  )
}